import React, { useEffect, useRef } from 'react';
import { path } from 'ramda';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import ListSubheader from '@material-ui/core/ListSubheader';
import { MenuClassKey } from '@material-ui/core/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

import { isDefined } from 'utils/helpers';
import useFieldValidation from 'hooks/validation/useFieldValidation';
import { TFieldValidatorFn, TValidationMode } from 'hooks/validation/types';
import { useMissingFieldError } from 'components/product/ProductUpdateForm/validation-context/ProductValidationContext';
import AiFieldVerdict from 'components/product/ProductUpdateForm/AiFieldVerdict';

const useStyles = makeStyles(theme => ({
  selectPaper: {
    '& .MuiMenu-list': {
      paddingTop: 0,
    },
  },
  searchWrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '8px 16px',
    backgroundColor: '#ffffff',
  },
}));

export interface IListItem {
  value?: string | number;
  key?: string;
  displayName?: string;
  suggested?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  group?: string;
}

const getListItemLabel = (item: IListItem) =>
  String(isDefined(path(['displayName'], item)) ? item.displayName : item.value ?? '');

interface IListDropDownProps {
  readOnly?: boolean;
  validatorMode?: TValidationMode;
  validator?: TFieldValidatorFn;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  listData?: IListItem[];
  handleChange?: (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
    child: React.ReactNode
  ) => void;
  value?: any;
  targetName?: string;
  style?: React.CSSProperties;
  formControlDownStyle?: React.CSSProperties;
  dropDownStyle?: Partial<ClassNameMap<MenuClassKey>>;
  displayEmpty?: boolean;
  disableLabel?: boolean;
  showSuggested?: boolean;
  showSearch?: boolean;
  errorText?: string;
  disabled?: boolean;
  required?: boolean;
  background?: string;
  forceRed?: boolean;
  onOpen?: () => void;
  // The field shows the failed AI review verdict itself, e.g. below its row.
  hideAiVerdict?: boolean;
}

const ListDropDown = ({
  hideAiVerdict,
  readOnly,
  validatorMode,
  validator,
  fullWidth = true,
  size = 'medium',
  label,
  listData,
  handleChange: handleChangeProp,
  errorText,
  value,
  targetName,
  style,
  formControlDownStyle,
  dropDownStyle,
  displayEmpty,
  disableLabel,
  showSuggested,
  showSearch,
  disabled: disabledProp,
  required,
  background = '#FFFFFF',
  forceRed,
  onOpen,
}: IListDropDownProps) => {
  const disabled = readOnly || disabledProp;
  const classes = useStyles();
  const searchWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');

  const validation = useFieldValidation(targetName || '', {
    mode: validatorMode,
    value,
    required,
    validator,
  });
  const keepRef = useRef({ validation });
  keepRef.current.validation = validation;

  let suggested: IListItem[] = [];
  if (showSuggested && listData) {
    suggested = listData.filter(x => x.suggested);
  }
  const containsText = (text?: string, searchText?: string) => {
    if (!searchText || searchText.trim() === '') {
      return true;
    }
    if (!text || text.trim() === '') {
      return false;
    }
    return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
  };
  const filteredData = React.useMemo(() => {
    if (!listData) {
      return [];
    }
    if (!searchText || searchText.trim() === '') {
      return listData;
    }
    return listData?.filter(x => containsText(getListItemLabel(x), searchText) || containsText(x?.group, searchText));
  }, [listData, searchText]);
  let isError = !!forceRed || !!(errorText && errorText.trim() !== '');
  let errorMessage = errorText;

  // FC-1950: retailer-required field that is still missing (no-op outside the product form).
  const missingFieldError = useMissingFieldError(targetName, touched && !open);
  if (missingFieldError) {
    isError = true;
    errorMessage = errorMessage || missingFieldError;
  }
  // For now, make [validation.mode] condition to make [useFieldValidation] stop working with old field
  if (validation.mode) {
    if (validation.error) {
      isError = !!validation.error;
      if (!errorText) {
        errorMessage = validation.error;
      }
    }
  }

  const handleOpen = () => {
    setSearchText('');
    setOpen(true);
    setTouched(true);
    onOpen?.();
  };

  const handleClose = (event: React.ChangeEvent<{}>) => {
    if (!searchWrapperRef.current?.contains(event.nativeEvent.target as Node)) {
      setOpen(false);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (!searchWrapperRef.current?.contains(event.nativeEvent.target as Node)) {
      handleChangeProp?.(event, child);
    }
  };

  useEffect(() => {
    if (touched && !open) {
      keepRef.current.validation.validate(value);
    }
  }, [value, open, touched]);

  const fontSize = size === 'small' ? 14 : 16;

  return (
    <div style={style}>
      <FormControl
        fullWidth={fullWidth}
        size={size}
        variant="outlined"
        error={isError}
        disabled={disabled}
        required={required}
        style={{ background, marginBottom: '10px', ...formControlDownStyle }}
      >
        {!disableLabel && <InputLabel id="simple-select-outlined-label">{label}</InputLabel>}

        <Select
          // error
          disabled={disabled}
          labelId="simple-select-outlined-label"
          id={targetName}
          value={value}
          open={open}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          label={label}
          displayEmpty={displayEmpty}
          name={targetName}
          // dropDownStyle={dropDownStyle}
          MenuProps={{
            classes: dropDownStyle, // TODO check
            autoFocus: !disabled && !!value,
            transitionDuration: 0,
            PaperProps: {
              className: showSearch ? classes.selectPaper : undefined,
            },
          }}
          SelectDisplayProps={{ style: { fontSize } }}
          inputProps={{
            'aria-invalid': isError,
            'aria-describedby': isError ? errorMessage : undefined,
          }}
        >
          {showSearch && (
            <div ref={searchWrapperRef} className={classes.searchWrapper}>
              <TextField
                fullWidth
                size="small"
                disabled={disabled}
                autoFocus={!disabled && !value}
                variant="outlined"
                placeholder="Type to search..."
                InputProps={{
                  autoComplete: 'new-password', // disable autocomplete and autofill (off not working in Chrome)
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { fontSize },
                }}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => {
                  if (e.key !== 'Escape') {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          )}
          {displayEmpty && !listData?.some(item => item.value === '') && (
            <MenuItem value={undefined} style={{ fontSize }}>
              <em style={{ visibility: 'hidden' }}>None</em>
            </MenuItem>
          )}
          {showSuggested && suggested && suggested.length > 0 && (
            <>
              <ListSubheader disableSticky={true}>Suggested</ListSubheader>
              {suggested.map((i, idx) => (
                <MenuItem value={i.value} key={i.key || `${i.value}-${idx}`} style={{ fontSize }}>
                  <em>{getListItemLabel(i)}</em>
                </MenuItem>
              ))}
            </>
          )}
          {showSuggested && suggested && suggested.length > 0 && (
            <ListSubheader disableSticky={true} style={{ fontSize }}>
              Other
            </ListSubheader>
          )}
          {filteredData &&
            filteredData.flatMap((i, idx) => {
              const prevGroup = idx > 0 ? filteredData[idx - 1].group : undefined;
              const nodes: React.ReactNode[] = [];
              if (i.group && i.group !== prevGroup) {
                nodes.push(
                  <ListSubheader disableSticky key={`group-${idx}-${i.group}`} style={{ fontSize }}>
                    {i.group}
                  </ListSubheader>
                );
              }
              nodes.push(
                <MenuItem value={i.value} key={i.key || `${i.value}-${idx}`} disabled={i.disabled} style={{ fontSize, ...i.style }}>
                  <em>{getListItemLabel(i)}</em>
                </MenuItem>
              );
              return nodes;
            })}
        </Select>
        {isError && (
          <FormHelperText id={targetName ? `${targetName}-error` : 'listdropdown-error'}>{errorMessage}</FormHelperText>
        )}
        {!hideAiVerdict && <AiFieldVerdict field={targetName} />}
      </FormControl>
    </div>
  );
};

export default ListDropDown;
